import { useState, type ReactNode } from "react";
import { ChartColumn, Download, File, Loader2, Table } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "~/components/ui/dialog";
import { ArtifactPreviewBody } from "~/pages/manifold-nextgen/components/conversations/artifacts/ArtifactPreviewBody";
import { ArtifactLayoutProvider } from "~/pages/manifold-nextgen/components/conversations/artifacts/ArtifactShell";
import type { ArtifactPreview } from "~/services/artifacts";
import type { StepState, WorkflowSpec } from "~/services/pinnedArtifacts";
import { LineageGraph } from "./LineageGraph";
import { LineageSteps } from "./LineageSteps";
import type { LineageNode } from "./workflowGraph";

const TypeIcon = ({ artifactType }: { artifactType: string }) => {
  const className = "size-6 text-muted-foreground";
  if (artifactType === "figure") return <ChartColumn className={className} />;
  if (artifactType === "dataframe") return <Table className={className} />;
  return <File className={className} />;
};

interface ArtifactLineagePanelProps {
  name: string;
  summary: string | null;
  artifactType: string;
  // The artifact itself, for the thumbnail and its expanded view; the type
  // icon stands in until it loads.
  preview?: ArtifactPreview;
  // Icon buttons beside the name, e.g. Download.
  headerActions?: ReactNode;
  // A status line under the summary, e.g. who shared it.
  statusLine?: ReactNode;
  // The Status section's rows: sharing and refreshing, as the caller allows.
  status?: ReactNode;
  recipe: WorkflowSpec | undefined;
  isLoading: boolean;
  // Why no recipe could be traced, already worded for the reader.
  recipeError?: string | null;
  stateByStep?: Map<string, StepState>;
}

/** The body of an artifact's lineage side panel: what it is, its sharing
 *  status, how it was made, and each step's code.
 *
 *  Selecting a node opens the step it belongs to, so the graph doubles as an
 *  index into the step list. Callers key this by artifact so the selection
 *  resets when the panel moves to another one.
 */
export const ArtifactLineagePanel = ({
  name,
  summary,
  artifactType,
  preview,
  headerActions,
  statusLine,
  status,
  recipe,
  isLoading,
  recipeError,
  stateByStep,
}: ArtifactLineagePanelProps) => {
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [openStep, setOpenStep] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);

  const selectNode = (node: LineageNode | null) => {
    setSelectedNode(node?.id ?? null);
    if (node?.stepIndex !== null && node?.stepIndex !== undefined) setOpenStep(node.stepIndex);
  };

  return (
    <div className="flex flex-col gap-5 p-4">
      <div className="flex items-start gap-3">
        {preview ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-label={`Expand ${name}`}
            title="Expand"
            className="relative h-[72px] w-24 shrink-0 cursor-zoom-in overflow-hidden rounded-lg border border-border bg-background hover:border-foreground/30"
          >
            {/* Drawn at four times the box's width and scaled down, so charts
                and tables lay out as they would in a card. Inert, because the
                whole box is the expand control. */}
            <div
              inert
              className="pointer-events-none absolute top-0 left-0 w-[384px] origin-top-left scale-[0.25]"
            >
              <ArtifactLayoutProvider layout="thumbnail">
                <ArtifactPreviewBody preview={preview} name={name} />
              </ArtifactLayoutProvider>
            </div>
          </button>
        ) : (
          <div className="flex h-[72px] w-24 shrink-0 items-center justify-center rounded-lg border border-border bg-background">
            <TypeIcon artifactType={artifactType} />
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-start gap-1">
            <span
              className="line-clamp-2 min-w-0 flex-1 text-[15px] font-semibold leading-[21px] break-words text-pretty"
              title={name}
            >
              {name}
            </span>
            {headerActions && <div className="-mt-1 -mr-1.5 flex">{headerActions}</div>}
          </div>
          {summary && (
            <span
              className="line-clamp-2 text-xs leading-snug text-muted-foreground"
              title={summary}
            >
              {summary}
            </span>
          )}
          {statusLine && <span className="text-xs text-muted-foreground">{statusLine}</span>}
        </div>
      </div>

      {status && (
        <section className="flex flex-col gap-2.5 border-t border-border pt-5">
          <h3 className="text-sm font-semibold">Status</h3>
          <div className="flex flex-col gap-3">{status}</div>
        </section>
      )}

      <section className="flex flex-col gap-2.5 border-t border-border pt-5">
        <h3 className="text-sm font-semibold">Lineage</h3>
        <p className="text-xs leading-normal text-muted-foreground">
          How this artifact was made: sources, the queries that retrieved data, and the code that
          transformed it. Select a node for details.
        </p>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2
              className="size-5 animate-spin text-muted-foreground"
              aria-label="Loading lineage"
            />
          </div>
        ) : recipeError ? (
          <p className="text-xs text-destructive">{recipeError}</p>
        ) : recipe ? (
          <LineageGraph spec={recipe} selectedId={selectedNode} onSelect={selectNode} />
        ) : null}
      </section>

      {recipe && !isLoading && (
        <section className="flex flex-col gap-2.5 border-t border-border pt-5">
          <h3 className="text-sm font-semibold">Steps</h3>
          <LineageSteps
            spec={recipe}
            openStep={openStep}
            onToggle={(index) => setOpenStep((current) => (current === index ? null : index))}
            stateByStep={stateByStep}
          />
        </section>
      )}

      {preview && (
        <Dialog open={expanded} onOpenChange={setExpanded}>
          <DialogContent
            className="flex h-[calc(100vh-48px)] w-[calc(100vw-48px)] max-w-none flex-col gap-0 overflow-hidden p-4 pt-2 sm:max-w-none"
            overlayClassName="bg-black/70"
          >
            <DialogTitle className="shrink-0 border-b border-border py-2 text-base font-semibold">
              {name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {summary ?? `Expanded view of ${name}`}
            </DialogDescription>
            <div className="min-h-0 flex-1 overflow-auto pt-4">
              <ArtifactLayoutProvider layout="expanded">
                <ArtifactPreviewBody preview={preview} name={name} />
              </ArtifactLayoutProvider>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

/** The fixed-width button that leads a Status row. */
/** The panel's header Download: the caller opens whichever dialog fits its scope. */
export const DownloadHeaderButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    type="button"
    variant="ghost"
    size="icon"
    onClick={onClick}
    aria-label="Download"
    title="Download"
    className="size-8 text-muted-foreground"
  >
    <Download className="size-4" />
  </Button>
);

export const LineageStatusButton = ({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex h-[30px] w-[104px] shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 text-[12.5px] font-medium text-violet-700 hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-violet-900 dark:bg-violet-950/50 dark:text-violet-300"
  >
    {children}
  </button>
);

interface LineageStatusRowProps {
  action?: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
}

/** One row of the panel's Status section: an optional button and what it
 *  says about the artifact. */
export const LineageStatusRow = ({ action, title, detail }: LineageStatusRowProps) => (
  <div className="flex items-center gap-3">
    {action}
    <div className="flex min-w-0 flex-1 flex-col gap-px text-[12.5px] leading-snug">
      <span className="text-foreground">{title}</span>
      {detail && <span className="truncate text-muted-foreground">{detail}</span>}
    </div>
  </div>
);
